import { inject } from '@angular/core';
import { CanDeactivateFn, Route, Routes } from '@angular/router';
import { Observable } from 'rxjs';
import { UnsavedChangesService } from '../services/unsaved-changes.service';
import { DirtyFormRegistry } from '../services/dirty-form-registry.service';

/**
 * Contract for a component that can hold unsaved edits. Implement on any routed component
 * to opt into the unsavedChangesGuard. Forms extending BaseFormComponent already implement it.
 */
export interface HasUnsavedChanges {
  /** True while the component holds edits that would be lost on navigation. */
  hasUnsavedChanges(): boolean;
  /** Optional custom discard message for the prompt. */
  unsavedChangesMessage?(): string | undefined;
}

/**
 * CanDeactivate guard: when the routed component reports unsaved changes — or any mounted form
 * registered with DirtyFormRegistry is dirty — show the shared discard prompt and block navigation
 * unless the user confirms. Attach to a route with `canDeactivate: [unsavedChangesGuard]`.
 *
 * Because it falls back to the registry, a routed page does NOT need to implement HasUnsavedChanges:
 * it is enough that the forms it hosts extend BaseFormComponent (which self-registers).
 */
export const unsavedChangesGuard: CanDeactivateFn<Partial<HasUnsavedChanges>> = (
  component
): boolean | Observable<boolean> => {
  const componentDirty =
    typeof component?.hasUnsavedChanges === 'function' && component.hasUnsavedChanges();
  const registryDirty = inject(DirtyFormRegistry).isAnyDirty();

  if (!componentDirty && !registryDirty) {
    return true;
  }

  const message =
    typeof component?.unsavedChangesMessage === 'function'
      ? component.unsavedChangesMessage()
      : undefined;
  return inject(UnsavedChangesService).confirmDiscard(true, message);
};

/**
 * Recursively attaches `unsavedChangesGuard` to every routed component in a route tree — including
 * routes loaded lazily via `loadChildren`. Apply once to the root route array so every form-hosting
 * page in the app is protected on navigation, with no per-route wiring.
 *
 * The guard is a no-op when nothing is dirty, so it is safe to add to non-form routes (lists,
 * dashboards, etc.).
 */
export function withUnsavedChangesGuard(routes: Routes): Routes {
  return routes.map((route) => {
    const next: Route = { ...route };
    const isLeaf = !route.children && !route.loadChildren;

    // Guard only leaf form-hosting routes — never layout/container routes. Otherwise leaving the app
    // shell would deactivate both the leaf and the layout, firing the prompt twice in one navigation.
    if ((route.component || route.loadComponent) && isLeaf) {
      next.canDeactivate = [...(route.canDeactivate ?? []), unsavedChangesGuard];
    }

    if (route.children) {
      next.children = withUnsavedChangesGuard(route.children);
    }

    if (route.loadChildren) {
      const loadChildren = route.loadChildren;
      next.loadChildren = () => {
        const loaded = loadChildren();
        return loaded instanceof Promise
          ? loaded.then((result) => (Array.isArray(result) ? withUnsavedChangesGuard(result) : result))
          : loaded;
      };
    }

    return next;
  });
}
